import React, {
  Context,
  createContext,
  FC,
  useReducer,
  useEffect,
  useContext,
  useMemo,
} from 'react';
import { useLocation } from 'react-router-dom';
import { isRoutePath, RouteItem } from '../../routes/RouteItem';
import { RoutesContext, RoutesAction, RoutesState } from './Routes.types';
import { applicationRoutes, trackerRoutes } from '../../routes/private';
import { routes as gogurtAppRoutes } from '../../gogurt/routes/private';
import { BrandStore } from '../brand/Brand.context';
import { AuthStore } from '../auth/Auth.context';
import { RegistrationStore } from '../registration';
import { isAccountPageHidden } from '../../pages/account/AccountFields.util';
import {
  isBusinessPageHidden,
  areBusinessFieldsHidden,
} from '../../pages/business/BusinessFields.util';
import {
  isFinancialPageHidden,
  areFinancialFieldsHidden,
} from '../../pages/financial/FinancialFields.util';
import {
  isPaymentsPageHidden,
  arePaymentsFieldsHidden,
} from '../../pages/payments/PaymentsFields.util';
import { isDocumentsPageHidden } from '../../pages/documents/Documents.util';
import { isGogurtSource } from '../../util/registration.util';

const RoutesStore = createContext(
  {} as RoutesContext,
) as Context<RoutesContext>;

type Reducer = (state: RoutesState, action: RoutesAction) => RoutesState;

const reducer: Reducer = (_, action) => {
  const isGogurt = action.payload?.registration
    ? isGogurtSource(action.payload?.registration)
    : action.mode === 'app';

  const routes = isGogurt
    ? gogurtAppRoutes
    : applicationRoutes;

  switch (action.mode) {
    case 'application':
    case 'app':
      return {
        mode: action.mode,
        routes: routes,
      };
    case 'tracker':
      return {
        mode: action.mode,
        routes: trackerRoutes,
      };
  }
};

const RoutesProvider: FC<{ children?: React.ReactNode }> = props => {
  const [state, dispatch] = useReducer<Reducer>(reducer, {
    mode: null!,
    routes: [],
  });
  const { state: { auth } } = useContext(AuthStore);
  const { state: { brand } } = useContext(BrandStore);
  const {
    state: { registration },
  } = useContext(RegistrationStore);
  const { pathname } = useLocation();
  const user = auth?.user;

  useEffect(() => {
    if (pathname.includes('/app/')) {
      dispatch({ mode: 'app', payload: { registration: registration! } });
    } else if (
      pathname.includes('/application') ||
      pathname.includes('/welcome')
    ) {
      dispatch({ mode: 'application', payload: { registration: registration! } });
    } else if (pathname.includes('tracker')) {
      dispatch({ mode: 'tracker', payload: { registration: registration! } });
    }
  }, [pathname, registration]);

  const filteredSidebarRoutes = useMemo(() => {
    if (brand == null
      || state.mode === 'tracker'
      // the Gogurt 'app' routes aren't currently hidden based on branding, return them as-is
      || state.mode === 'app'
    ) {
      return state.routes;
    }

    return state.routes.filter(route => {
      switch (route.title) {
        case 'Account':
          return !isAccountPageHidden(user?.acknowledgments);
        case 'Payments':
          return !(
            isPaymentsPageHidden(brand) ||
            arePaymentsFieldsHidden(brand, registration)
          );
        case 'Business':
          return !(
            isBusinessPageHidden(brand) ||
            areBusinessFieldsHidden(brand, registration)
          );
        case 'Business Owner/Financial':
          return !(
            isFinancialPageHidden(brand) ||
            areFinancialFieldsHidden(brand, registration)
          );
        case 'Documents/Statements':
          return !isDocumentsPageHidden(brand);
        default:
          return true;
      }
    });
  }, [state.routes, state.mode, user, registration, brand]);

  const currentRouteIndex = filteredSidebarRoutes.findIndex(r =>
    isRoutePath(r, pathname)
  );

  const currentRoute =
    state.mode !== 'tracker' && currentRouteIndex >= 0
      ? (filteredSidebarRoutes as RouteItem[])[currentRouteIndex]
      : null;

  const previousRoute =
    state.mode !== 'tracker' && currentRouteIndex > 0
      ? (filteredSidebarRoutes as RouteItem[])[currentRouteIndex - 1]
      : null;

  const nextRoute =
    state.mode !== 'tracker'
      ? (filteredSidebarRoutes as RouteItem[])[currentRouteIndex + 1]
      : null;

  const localContext: RoutesContext = {
    mode: state.mode,
    sidebarRoutes: filteredSidebarRoutes,
    currentRoute,
    previousRoute,
    nextRoute,
  };

  return (
    <RoutesStore.Provider value={localContext}>
      {props.children}
    </RoutesStore.Provider>
  );
};

export { RoutesStore, RoutesProvider };
